import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import "./admin-dashboard-filter-panel.scss";
import { getProviderProfiles } from "../../../../api/admin/provider-management";

export interface AdminDashboardFilterValues {
  all: boolean;
  provider: boolean;
  preneedPlanner: boolean;
  providerSearch: string;
  preneedPlannerSearch: string;
}

interface AdminDashboardFilterPanelProps {
  open: boolean;
  initialValues: AdminDashboardFilterValues;
  onClose: () => void;
  onApply: (filters: AdminDashboardFilterValues) => void;
  isFilterApplied: boolean;
}

const AdminDashboardFilterPanel: React.FC<AdminDashboardFilterPanelProps> = ({
  open,
  initialValues,
  onClose,
  onApply,
  isFilterApplied,
}) => {
  const [localFilters, setLocalFilters] =
    useState<AdminDashboardFilterValues>(initialValues);
  const [providerOptions, setProviderOptions] = useState<
    { id: string; label: string }[]
  >([]);
  const [plannerOptions, setPlannerOptions] = useState<
    { id: string; label: string }[]
  >([]);

  useEffect(() => {
    if (open) {
      setLocalFilters(initialValues);
    }
  }, [initialValues, open]);

  useEffect(() => {
    const fetchPartners = async () => {
      const response = await getProviderProfiles({ isDropdown: true });
      const partnerData = response?.data?.data?.data || [];

      setProviderOptions(
        partnerData
          .filter(
            (partner: any) =>
              partner.businessType?.toLowerCase() !== "preneed planner",
          )
          .map((partner: any) => ({
            id: partner.id,
            label: partner.businessName,
          })),
      );

      setPlannerOptions(
        partnerData
          .filter(
            (partner: any) =>
              partner.businessType?.toLowerCase() === "preneed planner",
          )
          .map((partner: any) => ({
            id: partner.id,
            label: partner.businessName,
          })),
      );
    };
    if (open) {
      fetchPartners();
    }
  }, [open]);

  const showProviderInput = useMemo(
    () => localFilters.provider,
    [localFilters.provider],
  );
  const showPlannerInput = useMemo(
    () => localFilters.preneedPlanner,
    [localFilters.preneedPlanner],
  );

  const handleTypeCheckboxChange = (
    key: "all" | "provider" | "preneedPlanner",
    checked: boolean,
  ) => {
    setLocalFilters((prev) => {
      if (!checked) {
        return { ...prev, [key]: false };
      }

      return {
        ...prev,
        all: key === "all",
        provider: key === "provider",
        preneedPlanner: key === "preneedPlanner",
        providerSearch: key === "provider" ? prev.providerSearch : "",
        preneedPlannerSearch:
          key === "preneedPlanner" ? prev.preneedPlannerSearch : "",
      };
    });
  };

  const handleReset = () => {
    const defaultFilters = {
      all: true,
      provider: false,
      preneedPlanner: false,
      providerSearch: "",
      preneedPlannerSearch: "",
    };
    setLocalFilters(defaultFilters);
    if (isFilterApplied) onApply(defaultFilters);
  };

  if (!open) return null;

  return (
    <div className="admin-dashboard-filter-overlay" onClick={onClose}>
      <aside
        className="admin-dashboard-filter-panel"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="admin-dashboard-filter-panel__head">
          <h5>Filter</h5>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close filter panel"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="admin-dashboard-filter-panel__body">
          <div className="admin-dashboard-filter-panel__section-title">
            TYPE
          </div>

          <label className="admin-dashboard-filter-panel__checkbox-row">
            <input
              type="checkbox"
              checked={localFilters.all}
              onChange={(event) =>
                handleTypeCheckboxChange("all", event.target.checked)
              }
            />
            <span>All</span>
          </label>

          <label className="admin-dashboard-filter-panel__checkbox-row">
            <input
              type="checkbox"
              checked={localFilters.provider}
              onChange={(event) =>
                handleTypeCheckboxChange("provider", event.target.checked)
              }
            />
            <span>Provider</span>
          </label>

          {showProviderInput && (
            <select
              className="admin-dashboard-filter-panel__dropdown-field"
              value={localFilters.providerSearch}
              onChange={(event) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  providerSearch: event.target.value,
                }))
              }
            >
              <option value="">Search provider</option>
              {providerOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          )}

          <label className="admin-dashboard-filter-panel__checkbox-row">
            <input
              type="checkbox"
              checked={localFilters.preneedPlanner}
              onChange={(event) =>
                handleTypeCheckboxChange("preneedPlanner", event.target.checked)
              }
            />
            <span>Preneed Planner</span>
          </label>

          {showPlannerInput && (
            <select
              className="admin-dashboard-filter-panel__dropdown-field"
              value={localFilters.preneedPlannerSearch}
              onChange={(event) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  preneedPlannerSearch: event.target.value,
                }))
              }
            >
              <option value="">Search preneed planner</option>
              {plannerOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="admin-dashboard-filter-panel__footer">
          <button
            type="button"
            className="admin-dashboard-filter-panel__cancel"
            onClick={handleReset}
          >
            Reset
          </button>
          <button
            type="button"
            className="admin-dashboard-filter-panel__apply"
            onClick={() => {
              onApply(localFilters);
              onClose();
              setLocalFilters(initialValues);
            }}
          >
            Apply
          </button>
        </div>
      </aside>
    </div>
  );
};

export default AdminDashboardFilterPanel;
